// client/src/crew/schedule.ts — the crew calendar's pure logic (Task C5). No Date constructor on
// calendar days: every day is an IST 'YYYY-MM-DD' string and arithmetic goes through
// shared/vmsDuty/civilDate. "Today" is INJECTED as the IST date (the owner may open the app in the
// UAE; the device date must never decide what is upcoming).
import { addDays, mondayOf, parseIso, weekdayIndex } from "@shared/vmsDuty/civilDate";

export interface CrewEvent {
  weddingId: number; serviceType: string; date: string; slot: "day" | "evening" | "other"; slotLabel: string;
  ceremonyStart: string | null; couple: string; venue: string; guestCount: number | null; variant: string | null;
  eventManager: { name: string; phone: string };
}
export interface CrewShift { date: string; kind: "duty" | "off"; duty: string | null; role: "mentor" | "learner" | "solo" | null; gardeningEvening: boolean; weekStart: string }
export interface CrewShiftWeek { weekStart: string; offDay: string | null; note: string | null }
export interface CrewSchedule { events: CrewEvent[]; shifts: CrewShift[]; shiftWeeks?: CrewShiftWeek[]; generatedAt: string }

export interface DayBucket { date: string; events: CrewEvent[]; shifts: CrewShift[] }

export const SERVICE_LABEL: Record<string, string> = {
  mc_anchor: "MC", fusion_music: "Fusion music", hostesses: "Hostesses", dance_program: "Dance",
  singarimelam: "Singarimelam", duty_roster: "Duty roster",
};
export const serviceLabel = (s: string) => SERVICE_LABEL[s] ?? s.replace(/_/g, " ");

/** Events + shifts merged into one bucket per IST day, ascending. */
export function dayBuckets(s: Pick<CrewSchedule, "events" | "shifts">): DayBucket[] {
  const m = new Map<string, DayBucket>();
  const get = (d: string) => { let b = m.get(d); if (!b) { b = { date: d, events: [], shifts: [] }; m.set(d, b); } return b; };
  for (const e of s.events) get(e.date).events.push(e);
  for (const sh of s.shifts) get(sh.date).shifts.push(sh);
  return Array.from(m.values()).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/** Upcoming = today (IST) and later, ascending. Past = before today, most recent first. */
export function splitUpcoming(buckets: DayBucket[], todayIST: string): { upcoming: DayBucket[]; past: DayBucket[] } {
  return {
    upcoming: buckets.filter((b) => b.date >= todayIST),
    past: buckets.filter((b) => b.date < todayIST).reverse(),
  };
}

/** Mon–Sun of the ISO week containing `dateIso` (IST calendar). */
export function isoWeekDays(dateIso: string): string[] {
  const mon = mondayOf(dateIso);
  return Array.from({ length: 7 }, (_, i) => addDays(mon, i));
}

/** The week page's title: "This week" only for the week that contains today (audit CREW-4). */
export function weekTitle(days: string[], todayIST: string): string {
  if (days.includes(todayIST)) return "This week";
  const thisMon = isoWeekDays(todayIST)[0];
  if (days[0] === addDays(thisMon, 7)) return "Next week";
  if (days[0] === addDays(thisMon, -7)) return "Last week";
  const [, m, d] = days[0].split("-").map(Number);
  return `Week of ${d} ${MONTH_SHORT[m - 1]}`;
}
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Month grid: whole Mon-first weeks covering the month; days outside it are null. */
export function monthGrid(year: number, month: number): Array<Array<string | null>> {
  const first = `${year}-${String(month).padStart(2, "0")}-01`;
  const weeks: Array<Array<string | null>> = [];
  let cursor = mondayOf(first);
  while (true) {
    const week = Array.from({ length: 7 }, (_, i) => {
      const d = addDays(cursor, i);
      return parseIso(d).m === month ? d : null;
    });
    if (week.every((d) => d === null) && weeks.length) break;
    weeks.push(week);
    cursor = addDays(cursor, 7);
    if (parseIso(cursor).m !== month && parseIso(addDays(cursor, -1)).m !== month) break;
  }
  return weeks.filter((w) => w.some(Boolean));
}

/** Days in the bucket list that should carry a dot on the month grid. */
export const dotDays = (buckets: DayBucket[]) => new Set(buckets.filter((b) => b.events.length || b.shifts.some((s) => s.kind === "duty")).map((b) => b.date));

/** More than one service type across the schedule → rows carry a service badge. */
export const needsServiceBadge = (events: CrewEvent[]) => new Set(events.map((e) => e.serviceType)).size > 1;

export const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const weekdayOf = (d: string) => WEEKDAY_SHORT[weekdayIndex(d)];

/** Slot → local IST wall-clock window, for the calendar file. null = all-day. */
export function slotWindow(slot: CrewEvent["slot"]): { start: string; end: string } | null {
  if (slot === "day") return { start: "110000", end: "150000" };
  if (slot === "evening") return { start: "180000", end: "220000" };
  return null;
}

const icsEscape = (s: string) => s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");

/** A one-event iCalendar file for "Add to calendar" (times are IST wall clock, TZID=Asia/Kolkata). */
export function icsFor(e: CrewEvent, nowStamp: string): string {
  const day = e.date.replace(/-/g, "");
  const w = slotWindow(e.slot);
  const lines = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//LA Mirage//Crew app//EN", "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:lamirage-crew-${e.weddingId}-${e.serviceType}@lamirage.co`,
    `DTSTAMP:${nowStamp}`,
    ...(w
      ? [`DTSTART;TZID=Asia/Kolkata:${day}T${w.start}`, `DTEND;TZID=Asia/Kolkata:${day}T${w.end}`]
      : [`DTSTART;VALUE=DATE:${day}`, `DTEND;VALUE=DATE:${addDays(e.date, 1).replace(/-/g, "")}`]),
    `SUMMARY:${icsEscape(`${serviceLabel(e.serviceType)} — ${e.couple || "LA Mirage event"}`)}`,
    `LOCATION:${icsEscape(e.venue || "LA Mirage Koratty")}`,
    `DESCRIPTION:${icsEscape([e.slotLabel, e.ceremonyStart ? `Ceremony ${e.ceremonyStart}` : "", `Event manager: ${e.eventManager.name} ${e.eventManager.phone}`].filter(Boolean).join("\n"))}`,
    "END:VEVENT", "END:VCALENDAR",
  ];
  return lines.join("\r\n") + "\r\n";
}

export const icsDataUrl = (ics: string) => `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
export const DIRECTIONS_URL = "https://maps.google.com/?q=LA+Mirage+Koratty";

/** Web paths that belong to a crew-token session: home, the crew screens and the sign-in screens (CREW-3). */
export function isCrewWebPath(pathname: string): boolean {
  return pathname === "/" || /^\/(crew|login)(\/|$)/.test(pathname);
}
